import React, { useRef, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import {
  mdiSortAscending,
  mdiSortDescending,
  mdiClose,
  mdiArrowUp,
  mdiArrowDown,
} from "@mdi/js";
import { Button, cn } from "@heroui/react";
import { contractorsStore } from "@/stores/ContractorsStore";
import { popoverStore } from "@/stores/PopoverStore";

const POPOVER_ID = "contractors-sort";

const SORTABLE_COLUMNS = [
  { id: "name", label: "Название" },
  { id: "address", label: "Адрес" },
  { id: "bookingsCount", label: "Бронирования" },
  { id: "rating", label: "Рейтинг" },
  { id: "revenue", label: "Доход" },
  { id: "commission", label: "Комиссия" },
  { id: "pendingPayout", label: "К оплате" },
];

const SortPanel: React.FC = observer(() => {
  const isOpen = popoverStore.isOpen(POPOVER_ID);
  const isDisabled = contractorsStore.isUiBusy;
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { sortDescriptor } = contractorsStore;

  // Position panel; recalculate on window resize to prevent jump
  const positionPanel = useCallback(() => {
    if (anchorRef.current && panelRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      panelRef.current.style.top = `${rect.bottom + 8}px`;
      panelRef.current.style.left = `${rect.left}px`;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    positionPanel();
    window.addEventListener("resize", positionPanel);
    return () => window.removeEventListener("resize", positionPanel);
  }, [isOpen, positionPanel]);

  // Close on outside click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (
      panelRef.current &&
      !panelRef.current.contains(e.target as Node) &&
      anchorRef.current &&
      !anchorRef.current.contains(e.target as Node)
    ) {
      popoverStore.close(POPOVER_ID);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, handleOutsideClick]);

  return (
    <div className="sc-sort-anchor" ref={anchorRef}>
      <Button
        isIconOnly
        variant="tertiary"
        aria-label="Сортировка"
        isDisabled={isDisabled}
        onPress={() => popoverStore.toggle(POPOVER_ID)}
        className={
          isOpen ? "filter-btn--active icon-btn--card" : "icon-btn--card"
        }
      >
        <Icon
          path={
            sortDescriptor.direction === "descending"
              ? mdiSortDescending
              : mdiSortAscending
          }
          size={0.85}
        />
      </Button>

      {isOpen && (
        <div className="sc-sort-panel" ref={panelRef}>
          <div className="sc-sort-panel__header">
            <span className="sc-sort-panel__title">Сортировка</span>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              aria-label="Закрыть сортировку"
              onPress={() => popoverStore.close(POPOVER_ID)}
            >
              <Icon path={mdiClose} size={0.7} />
            </Button>
          </div>

          <div className="sc-sort-panel__body">
            {SORTABLE_COLUMNS.map((col) => {
              const isActive = sortDescriptor.column === col.id;
              return (
                <div
                  key={col.id}
                  className={cn(
                    "sc-sort-panel__item",
                    isActive && "sc-sort-panel__item--active",
                  )}
                >
                  <span className="sc-sort-panel__item-label">{col.label}</span>
                  <div className="sc-sort-panel__item-actions">
                    <Button
                      isIconOnly
                      size="sm"
                      variant={
                        isActive && sortDescriptor.direction === "ascending"
                          ? "primary"
                          : "tertiary"
                      }
                      aria-label={`${col.label} по возрастанию`}
                      onPress={() => {
                        contractorsStore.setSortDescriptor({
                          column: col.id,
                          direction: "ascending",
                        });
                      }}
                    >
                      <Icon path={mdiArrowUp} size={0.65} />
                    </Button>
                    <Button
                      isIconOnly
                      size="sm"
                      variant={
                        isActive && sortDescriptor.direction === "descending"
                          ? "primary"
                          : "tertiary"
                      }
                      aria-label={`${col.label} по убыванию`}
                      onPress={() => {
                        contractorsStore.setSortDescriptor({
                          column: col.id,
                          direction: "descending",
                        });
                      }}
                    >
                      <Icon path={mdiArrowDown} size={0.65} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

SortPanel.displayName = "SortPanel";
export default SortPanel;
